import React, { useState, useRef } from 'react';
// prettier-ignore
import { Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress, IconButton } from '@mui/material';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { WBSChildAccordion } from './WBSChildAccordion.jsx';
import { fetchLevel2WBS } from '../../services/FetchLevel2.js';
import { getBand } from '../../utils/_helpers.js';
import { CopyAlert } from '../shared/CopyAlert.jsx';
import { AddCostObjectDialog } from '../shared/AddCostObjectDialog.jsx';
import { Level1EmptyDialog } from '../shared/Level1EmptyDialog.jsx';
import { Alert } from '../shared/Alert.jsx';
import { ChangeErrorAlert } from '../shared/ChangeErrorAlert.jsx';
import { ImageButton } from '../shared/Image.jsx';
import { BasePopup } from '../shared/BasePopup.jsx';

export const WBSAccordion = ({ wbs, accordionIdx, context }) => {
    const getLevel1Details = context.get('Get Level 1 Details - WBS');
    const getLevel1ForEmpty = context.get('Get Level 1 Empty - WBS');
    const linkImage = context.get('Link Image');
    const [expanded, setExpanded] = useState(false);
    const [level1Empty, setLevel1Empty] = useState([]);
    const [filteredLevel1Empty, setFilteredLevel1Empty] = useState([]);
    const [openAddWBSDialog, setOpenAddWBSDialog] = useState(false);
    const [openLevel1EmptyDialog, setOpenLevel1EmptyDialog] = useState(false);
    const [openCopyAlert, setOpenCopyAlert] = useState(false);
    const [childAccordionExpanded, setChildAccordionExpanded] = useState([]);
    const isFirstRun = useRef(true);
    const [triggerChange, setTriggerChange] = useState(false);

    const [openAlert, setOpenAlert] = useState(false);
    const [openNoReleasePopup, setNoReleaseOpenPopup] = useState(false);
    const [openErrorAlert, setOpenErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [level2, setLevel2] = useState([]);
    let levelCount = 1;

    const [loading, setLoading] = useState(true);

    const fetchLevel1 = async () => {
        const [triggerInput, costElement] = getLevel1Details.getInputs();

        const [Success, Error, array] = getLevel1Details.getOutputs();

        const getLevel1Details$ = context
            .runner(getLevel1Details)
            .set(costElement, wbs.PrnNo ? wbs.PrnNo : wbs.CostElementId)
            .trigger(triggerInput);

        await getLevel1Details$.waitFor(Success, Error);
        const response = getLevel1Details$.get(array);
        if (array.length === 0) {
            throw new Error('Error while fetching SAP data');
        }
        return [...response];
    };

    const fetchLevel2BasedOnLevel1 = async () => {
        setLoading(true);
        const level1Response = await fetchLevel1();

        Promise.all(
            level1Response.map((item) => {
                return fetchLevel2WBS(
                    context,
                    item.PrnNo ? item.PrnNo : item.CostElementId,
                    item.RelGroup,
                    item.RelStrategy,
                    item.RelCode
                );
            })
        ).then((result) => {
            const level2Map = result
                .flatMap((flatData) => flatData)
                .reduce(
                    (entryMap, item) => entryMap.set(item.PriceRange, [...(entryMap.get(item.PriceRange) || []), item]),
                    new Map()
                );
            const level2MapSorted = new Map(
                [...level2Map.entries()].sort((a, b) => {
                    return Number(+a[0].match(/^(\D*)(\d+)/)[2] - +b[0].match(/^(\D*)(\d+)/)[2]);
                })
            );

            const level2Unique = new Map();
            level2MapSorted.forEach((level3, key) => {
                const seen = new Set();
                const level3Filtered = level3.filter((entry) => {
                    const key = `${entry.RelCode}__${entry.RelGroup}`;
                    const keep = !seen.has(key);
                    if (keep) {
                        seen.add(key);
                    }
                    return keep;
                });
                level2Unique.set(key, level3Filtered);
            });
            setLevel2(level2Unique);

            if (isFirstRun.current) {
                const expandedChildrenState = new Map();
                for (const entry of level2Unique) {
                    expandedChildrenState.set(entry[0], new Array(entry[1].length).fill(false));
                }
                setChildAccordionExpanded(expandedChildrenState);
                isFirstRun.current = false;
            }
            expandLevel2WithLevel1Empty(filteredLevel1Empty);
            setLoading(false);
        });
    };

    const handleExpandChange = (panel, costElement) => async (event, isExpanded) => {
        if (isExpanded === true) {
            fetchLevel2BasedOnLevel1();
        }
        setExpanded(isExpanded ? panel : false);
        event.stopPropagation();
    };

    const handleCloseNoReleasePopup = () => {
        setNoReleaseOpenPopup(false);
    };

    const handleGetLevel1ForEmpty = (selectedPlant, selectedPurchasingGroup, selectedPurchOrg) => {
        const purchasingGroupCode = selectedPurchasingGroup === null ? '' : selectedPurchasingGroup.PurchGroup;
        const [triggerInputLevel1Empty, plantId, purchasingGroup] = getLevel1ForEmpty.getInputs();

        // eslint-disable-next-line no-unused-vars
        const [SuccessLevel1Empty, ErrorLevel1Empty, arrayLevel1Empty] = getLevel1ForEmpty.getOutputs();

        const getLevel1ForEmpty$ = context
            .runner(getLevel1ForEmpty)
            .set(plantId, selectedPlant.PlantCode)
            .set(purchasingGroup, purchasingGroupCode)
            .trigger(triggerInputLevel1Empty);

        getLevel1ForEmpty$.waitFor(SuccessLevel1Empty).then(() => {
            if (!getLevel1ForEmpty$.get(arrayLevel1Empty) || !getLevel1ForEmpty$.get(arrayLevel1Empty).length) {
                setNoReleaseOpenPopup(true);
            } else {
                setLevel1Empty(getLevel1ForEmpty$.get(arrayLevel1Empty));
                setOpenLevel1EmptyDialog(true);
            }
        });
    };

    const handleAddLevel1ForEmpty = (checkedState, level1DetailsForEmpty) => {
        const filteredLevel1DetailsForEmpty = level1DetailsForEmpty.filter((obj, idx) => checkedState[idx] === true);
        const level3Array = [...level2].map(([priceRange, level3]) => level3).flat();
        const filteredLevel1DetailsForExisting = filteredLevel1DetailsForEmpty.filter((level1ForEmptyObj, idx) => {
            return !level3Array.some((level3Obj, i) => {
                return (
                    level1ForEmptyObj.RelCode === level3Obj.RelCode &&
                    level1ForEmptyObj.RelGroup === level3Obj.RelGroup &&
                    level1ForEmptyObj.RelStrategy === level3Obj.RelStrategy &&
                    level1ForEmptyObj.PriceRange === level3Obj.PriceRange
                );
            });
        });
        const wbsId = wbs.PrnNo ? wbs.PrnNo : wbs.CostElementId;
        const filteredLevel1DetailsForEmptyWithId = filteredLevel1DetailsForExisting.map((obj, idx) => {
            obj.CostElementId = wbsId;
            return obj;
        });
        setFilteredLevel1Empty(filteredLevel1DetailsForEmptyWithId);
        expandLevel2WithLevel1Empty(filteredLevel1DetailsForEmptyWithId);
    };

    const handleCopy = (e) => {
        navigator.clipboard.writeText(wbs.PrnNo ? `${wbs.PrnNo}` : `${wbs.CostElementId}`);
        setOpenCopyAlert(true);
        e.stopPropagation();
    };

    const handleCloseCopyAlert = (e) => {
        setOpenCopyAlert(false);
        e.stopPropagation();
    };

    const handleOpenDialog = (e) => {
        setOpenAddWBSDialog(true);
        e.stopPropagation();
    };

    const handleCloseDialog = (e) => {
        setOpenAddWBSDialog(false);
    };

    const handleCloseLevel1Dialog = (e) => {
        setOpenLevel1EmptyDialog(false);
    };

    const handleCloseAlert = () => {
        setOpenAlert(false);
        setOpenErrorAlert(false);
        setErrorMessage('');
    };

    const expandLevel2WithLevel1Empty = (filteredLevel1Empty) => {
        if (filteredLevel1Empty.length === 0) {
            return;
        }
        const newLevel2 = new Map([...level2.entries()]);
        const newExpandedState = new Map([...childAccordionExpanded.entries()]);
        filteredLevel1Empty.forEach((obj, idx) => {
            if (newLevel2.has(obj.PriceRange)) {
                const level3 = newLevel2.get(obj.PriceRange);
                if (!level3.includes(obj)) {
                    level3.push(obj);
                    newExpandedState.get(obj.PriceRange).push(false);
                }
            } else {
                newLevel2.set(obj.PriceRange, [obj]);
                newExpandedState.set(obj.PriceRange, [false]);
            }
        });
        setLevel2(newLevel2);
        setChildAccordionExpanded(newExpandedState);
    };

    return (
        <Accordion
            className={'accordion'}
            sx={{ boxShadow: 'none' }}
            key={accordionIdx}
            disableGutters={true}
            expanded={expanded === 'panel' + accordionIdx}
            onChange={handleExpandChange('panel' + accordionIdx, wbs.PrnNo ? wbs.PrnNo : wbs.CostElementId)}
            TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary
                className={'accordion-summary'}
                expandIcon={<ExpandCircleDownIcon color="primary" />}
                classes={{
                    content: 'accordion-summary-no-margin',
                }}
            >
                <div className={'flex-row-container'} style={{ width: '100%' }}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: '800', color: 'rgb(102, 110, 114)' }}>
                            WBS{' '}
                            {
                                wbs.PrnNo
                                    ? wbs.PrnNo
                                    : wbs.CostElementId /* TODO investigate whether ref to cost element can be removed here */
                            }
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgb(102, 110, 114)', marginLeft: '5px' }}>
                            {
                                wbs.CostElementDescription &&
                                    `- ${wbs.CostElementDescription}` /* TODO investigate whether ref to cost element can be removed here */
                            }
                            {wbs.PrnName && `- ${wbs.PrnName}`}
                            {wbs.WbsElement && ` (WBS ELEMENT ${wbs.WbsElement})`}
                        </Typography>
                        <IconButton onClick={(e) => handleCopy(e)} size="small" sx={{ marginLeft: '1vw' }}>
                            <ContentCopyIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </div>
                    {openCopyAlert && (
                        <CopyAlert open={openCopyAlert} handleClose={handleCloseCopyAlert} title={'WBS'} />
                    )}
                    {expanded && (
                        // TODO move this component to DRAW component when redoing UI for WBS/Cost Center accordions
                        <ImageButton
                            image={linkImage}
                            width={48}
                            height={24}
                            padding={3}
                            tooltipText="Add new WBS to Release Strategies"
                            tooltipPlacement="bottom"
                            disabled={localStorage.getItem('edit') !== 'true'}
                            onClick={(e) => handleOpenDialog(e)}
                            style={{ marginRight: '20px' }}
                        />
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails sx={{ height: '100%', padding: '0px' }}>
                {loading || level2.size === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            width: '100%',
                            height: '100%',
                            marginLeft: '28px',
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress color="primary" sx={{ margin: '0 1vw 0 0' }} />
                                <Typography variant="body1" color="primary">
                                    Loading...
                                </Typography>
                            </>
                        ) : (
                            <Typography
                                variant="body1"
                                sx={{
                                    padding: '1vh 0',
                                    color: 'rgb(102, 110, 114)',
                                    fontStyle: 'italic',
                                    fontWeight: 700,
                                }}
                            >
                                No approver assigned to the WBS
                            </Typography>
                        )}
                    </div>
                ) : (
                    [...level2].map(([priceRange, level3]) => {
                        return (
                            <Accordion
                                className={'accordion'}
                                sx={{ boxShadow: 'none' }}
                                key={priceRange}
                                disableGutters={true}
                                defaultExpanded={childAccordionExpanded.get(priceRange).includes(true)}
                                TransitionProps={{ unmountOnExit: true }}
                            >
                                <AccordionSummary
                                    className={'accordion-summary'}
                                    expandIcon={<ExpandMoreIcon size="large" sx={{ color: 'rgb(102, 110, 114)' }} />}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flex: 1,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                            }}
                                        >
                                            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: '600' }}>
                                                Level {levelCount++} - [{getBand(priceRange)}]
                                            </Typography>
                                        </div>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails sx={{ width: 'auto', padding: '0px 13px 8px' }}>
                                    {childAccordionExpanded &&
                                        level3.map((obj, idx) => {
                                            return (
                                                <WBSChildAccordion
                                                    key={
                                                        idx +
                                                        obj.RelCode +
                                                        obj.RelCodeTxt +
                                                        obj.RelGroup +
                                                        obj.RelGrpTxt +
                                                        obj.RelStrategy +
                                                        obj.RelStrategyTxt
                                                    }
                                                    childAccordionIdx={idx}
                                                    childAccordionDetails={obj}
                                                    wbs={wbs}
                                                    context={context}
                                                    priceRange={priceRange}
                                                    setChildAccordionExpanded={setChildAccordionExpanded}
                                                    childAccordionExpanded={childAccordionExpanded}
                                                    fetchLevel2BasedOnLevel1={fetchLevel2BasedOnLevel1}
                                                    triggerChange={triggerChange}
                                                    setTriggerChange={setTriggerChange}
                                                    setOpenAlert={setOpenAlert}
                                                    setOpenErrorAlert={setOpenErrorAlert}
                                                    setErrorMessage={setErrorMessage}
                                                />
                                            );
                                        })}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                )}
            </AccordionDetails>
            {openAddWBSDialog && (
                <AddCostObjectDialog
                    open={openAddWBSDialog}
                    objectType={'WBS'}
                    handleCloseDialog={handleCloseDialog}
                    handleGetLevel1ForEmpty={handleGetLevel1ForEmpty}
                    costObject={wbs}
                    context={context}
                />
            )}
            {openLevel1EmptyDialog && level1Empty.length > 0 && (
                <Level1EmptyDialog
                    title={'WBS'}
                    open={openLevel1EmptyDialog}
                    handleCloseLevel1Dialog={handleCloseLevel1Dialog}
                    handleAddLevel1ForEmpty={handleAddLevel1ForEmpty}
                    level1Empty={level1Empty}
                    context={context}
                />
            )}
            {openAlert && <Alert open={openAlert} handleClose={handleCloseAlert} />}
            {openNoReleasePopup && (
                <BasePopup
                    showTitleBanner="No release strategies found"
                    title="No release strategies found"
                    content="No release strategies found"
                    handleCloseDialog={handleCloseNoReleasePopup}
                />
            )}
            {openErrorAlert && (
                // TODO replace with Show Backend Alert brick call (from relevant lambda) to simplify code
                <ChangeErrorAlert open={openErrorAlert} handleClose={handleCloseAlert} errorMessage={errorMessage} />
            )}
        </Accordion>
    );
};
